import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-audit-site-images' as const;

/**
 * Schema for a site asset missing alt text
 */
export const assetMissingAltSchema = z.object({
  assetId: z.string().describe('The asset ID'),
  assetUrl: z.string().describe('The S3 URL of the asset'),
  currentAlt: z
    .string()
    .nullable()
    .describe('Current alt text (null if missing)'),
});

export type AssetMissingAlt = z.infer<typeof assetMissingAltSchema>;

/**
 * Schema for a CMS collection item image missing alt text
 */
export const collectionItemImageSchema = z.object({
  itemId: z.string().describe('The CMS collection item ID'),
  fieldSlug: z.string().describe('The CMS field slug containing the image'),
  collectionId: z.string().describe('The CMS collection ID'),
  fieldType: z
    .enum(['ImageRef', 'MultiImage', 'RichText'])
    .optional()
    .describe('The type of CMS field'),
  imageRef: z.object({
    fileId: z.string().optional().describe('The file ID of the image'),
    url: z.string().describe('The URL of the image'),
    alt: z.string().nullable().describe('Current alt text (null if missing)'),
  }),
});

export type CollectionItemImage = z.infer<typeof collectionItemImageSchema>;

/**
 * Input arguments for the audit-site-images tool
 */
export const auditSiteImagesArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site to audit'),
  pageIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional list of specific page IDs to audit. If not provided, audits all pages.'
    ),
  includeStaticPages: z
    .boolean()
    .optional()
    .describe(
      'Whether to include static pages in the audit. Defaults to true.'
    ),
  includeDynamicPages: z
    .boolean()
    .optional()
    .describe(
      'Whether to include dynamic/CMS pages in the audit. Defaults to true.'
    ),
  localeId: z
    .string()
    .optional()
    .describe('Optional locale ID for localized content'),
  /**
   * Internal-only param used by the recurring SEO audit pipeline to skip
   * assets and CMS items that haven't changed since the previous audit.
   * Not surfaced to the LLM (it's omitted from the tool description).
   */
  lastAuditDate: z
    .date()
    .optional()
    .describe(
      'Internal: cutoff timestamp for incremental filtering of assets and CMS items.'
    ),
  /**
   * Internal-only flag mirrored from the seoAudit job payload. When true, the
   * `lastAuditDate` filter is ignored even if provided.
   */
  forceFullScan: z
    .boolean()
    .optional()
    .describe(
      'Internal: when true, bypass incremental filtering and re-evaluate everything.'
    ),
  fullScanCollectionIds: z
    .array(z.string())
    .optional()
    .describe(
      'Internal: CMS collection IDs to scan without incremental filtering.'
    ),
});

export type AuditSiteImagesArgs = z.infer<typeof auditSiteImagesArgsSchema>;

/**
 * Return type for the audit-site-images tool
 */
export const auditSiteImagesResultSchema = z.object({
  siteId: z.string(),
  totalAssetsFound: z
    .number()
    .describe('Total number of image assets found on the site'),
  assetsWithMissingAlt: z
    .number()
    .describe('Total number of assets and CMS images with missing alt text'),
  assets: z
    .array(assetMissingAltSchema)
    .describe('Site image assets missing alt text'),
  cmsImages: z
    .array(collectionItemImageSchema)
    .describe('CMS collection item images missing alt text'),
  errors: z.array(z.string()).describe('Any errors encountered during audit'),
  /**
   * Non-fatal warnings (e.g. a CMS collection was truncated at the 10k item
   * cap and may have missed images). These are surfaced separately from
   * `errors` so the audit can still report success while the operator has
   * visibility into partial scans.
   */
  warnings: z
    .array(z.string())
    .optional()
    .describe('Non-fatal warnings encountered during audit'),
});

export type AuditSiteImagesResult = z.infer<typeof auditSiteImagesResultSchema>;

/**
 * Tool manifest for auditing all images across a site with deduplication
 */
export const auditSiteImagesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Audit site images',
  prettyStatus: {
    executed: 'Audited site for images',
    executing: 'Auditing site for images',
    failed: 'Failed to audit site images',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Audits all images across a site (or specified pages) and identifies those 
      missing alt text. This tool deduplicates images by URL, so each unique image 
      is only reported once even if it appears on multiple pages.
      
      The tool tracks which pages and elements use each image, making it efficient 
      for site-wide SEO audits. Use this tool before calling generate-image-alt-text 
      to get a deduplicated list of images that need alt text.
      
      Supports both static pages (with direct image assets) and dynamic/CMS pages 
      (with collection-bound images).
    `,
    inputSchema: auditSiteImagesArgsSchema,
  },
  type: 'server',
  permission: [{resource: 'SiteFile', action: 'manage'}],
  confirmBeforeExecuting: false,
  timeout: 600000, // 10 minutes - site-wide audit can take time
});
